/**
 * Payroll Submissions — client types and API calls.
 * Mirrors api/src/payroll/types.ts. Uses the existing apiFetch (401 → Entra login).
 */
import { apiFetch } from "../api";

export type MonthStatus = "not_started" | "uploaded" | "submitted" | "posted";
export type JournalStatus = "pending" | "posting" | "draft_created" | "failed";
export type DeptClass = "Direct" | "Semi Direct" | "Indirect";

export interface PayMonth {
  periodNo: number;
  monthLabel: string;
  periodCode: string;
  status: MonthStatus;
  runId: string | null;
}
export interface FinancialYear { fyCode: string; status: string; months: PayMonth[] }

export interface JournalLine {
  account: string;
  department: string | null;
  costCentre: string | null;
  description: string;
  amount: number;
}

export interface JournalRecord {
  importDocRef: number;
  payrollName: string;
  iplicitDept: string;
  description: string;
  periodCode: string;
  docDate: string;
  value: number;
  lines: JournalLine[];
  status: JournalStatus;
  iplicitDocNo: string | null;
  errorText: string | null;
  edited: boolean;
}

export interface RunDept {
  payrollName: string;
  people: number;
  taxablePay: number;
  holidayPay: number;
  tax: number;
  eeNI: number;
  erNI: number;
  preTaxPension: number;
  postTaxPension: number;
  erPension: number;
  postTaxAdd: number;
  aeo: number;
  studentLoan: number;
  netPay: number;
  iplicitDept: string | null;
  class: DeptClass | null;
  costCentre: string | null;
  excluded: boolean;
}

export interface RunTotals {
  gross: number; erNI: number; erPension: number; employerCost: number;
  payeAndEeNI: number; netPay: number; people: number;
}

export interface Run {
  id: string;
  fyCode: string;
  periodCode: string;
  monthLabel: string;
  entity: string;
  fileName: string;
  payeMonth: number | null;
  runDate: string;
  status: "parsed" | "submitting" | "submitted" | "superseded";
  totals: RunTotals;
  anomalies: string[];
  excluded: string[];
  parsedUtc: string;
  parsedBy: string;
  departments: RunDept[];
  journals: JournalRecord[];
}

export interface ValidateResult {
  ok: boolean;
  journals: number;
  lines: number;
  problems: Array<{ department: string; error: string }>;
}

/* ---- Import reconciliation (preview) ---- */
export interface DeptReconRow {
  payrollName: string;
  kind: "new" | "mismapped";
  reason: string;
  current: { iplicitDept: string | null; class: DeptClass | null; costCentre: string | null } | null;
}
export interface Reconciliation {
  clean: boolean;
  needsReview: DeptReconRow[];
  ignored: string[];
  activeCount: number;
  iplicitDepartments: Array<{ code: string; description: string }>;
  costCentres: Array<{ code: string; name: string }>;
}
export interface ImportPreview {
  reconciliation: Reconciliation;
  summary: {
    period: string;
    monthLabel: string;
    activeDepartments: number;
    people: number;
    payeMonth: number | null;
    runDate: string;
  };
}
/** One corrected/created department row, applied to the admin table on commit. */
export interface DeptEdit {
  payrollName: string;
  iplicitDept: string;
  class: DeptClass;
  costCentre: string;
}

export const getYears = () => apiFetch<{ years: FinancialYear[] }>("/api/payroll/years").then((r) => r.years);

export const getRunForPeriod = async (periodCode: string): Promise<Run | null> => {
  try {
    const r = await apiFetch<{ run: Run }>(`/api/payroll/runs?period=${periodCode}`);
    return r.run;
  } catch (e: any) {
    if (e?.status === 404) return null;
    throw e;
  }
};

export const getRun = (id: string) => apiFetch<{ run: Run }>(`/api/payroll/runs/${id}`).then((r) => r.run);

/**
 * Multipart POST helper. apiFetch is JSON-only, and a 401 on these must bounce to
 * Entra just like apiFetch does; a non-OK response throws with the server's message.
 */
async function postForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: "POST", body: form });
  if (res.status === 401) {
    const to = window.location.pathname + window.location.search;
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(to)}`;
    return new Promise<T>(() => {});
  }
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((payload as any)?.error ?? `Request failed (${res.status})`);
  return payload as T;
}

/** Phase 1: parse + reconcile departments against the admin table. Commits nothing. */
export async function previewRun(file: File, periodCode: string): Promise<ImportPreview> {
  const body = new FormData();
  body.append("file", file);
  body.append("period", periodCode);
  return postForm<ImportPreview>("/api/payroll/runs/preview", body);
}

/**
 * Phase 2: apply any reviewed department edits, then parse + persist the run.
 * `overwrite` (default true) decides whether reviewed values replace existing
 * department rows or only create genuinely new ones.
 */
export async function commitRun(
  file: File, periodCode: string, deptEdits: DeptEdit[] = [], overwrite = true
): Promise<Run> {
  const body = new FormData();
  body.append("file", file);
  body.append("period", periodCode);
  if (deptEdits.length) body.append("deptEdits", JSON.stringify(deptEdits));
  body.append("overwrite", overwrite ? "true" : "false");
  return (await postForm<{ run: Run }>("/api/payroll/runs", body)).run;
}

export const deleteRun = (id: string) => apiFetch<{ ok: true }>(`/api/payroll/runs/${id}`, { method: "DELETE" });

export const validateRun = (id: string) =>
  apiFetch<ValidateResult>(`/api/payroll/runs/${id}/validate`, { method: "POST" });

export const submitRun = (id: string) =>
  apiFetch<{ ok: boolean; posted: number; failed: number; run: Run }>(`/api/payroll/runs/${id}/submit`, { method: "POST" });

export const retryRun = (id: string) =>
  apiFetch<{ ok: boolean; posted: number; failed: number; run: Run }>(`/api/payroll/runs/${id}/retry`, { method: "POST" });

export const saveJournalEdit = (
  runId: string, ref: number, body: { lines: JournalLine[]; periodCode?: string; docDate?: string }
) => apiFetch<{ ok: true; run: Run }>(`/api/payroll/runs/${runId}/journals/${ref}`, {
  method: "PATCH",
  body: JSON.stringify(body),
});

/* ---- Admin reference data ---- */
export interface NominalRow {
  account: string; name: string;
  role: "gross" | "er_ni" | "er_pension" | "paye_control" | "pension_control" | "sundry_control" | "net_control";
  class: DeptClass | null; side: "DR" | "CR"; active: boolean;
}
export interface DeptMapping {
  payrollName: string; iplicitDept: string; class: DeptClass; costCentre: string; active: boolean;
}
export interface CostCentreRow {
  code: string; name: string; used_for: string | null; is_default: boolean; active: boolean;
}

export const getNominals = () => apiFetch<{ nominals: NominalRow[] }>("/api/payroll/nominals").then((r) => r.nominals);
export const saveNominal = (row: NominalRow) =>
  apiFetch<{ ok: true }>("/api/payroll/nominals", { method: "PATCH", body: JSON.stringify(row) });

export const getDepartments = () => apiFetch<{ departments: DeptMapping[] }>("/api/payroll/departments").then((r) => r.departments);
export const saveDepartment = (row: DeptMapping) =>
  apiFetch<{ ok: true }>("/api/payroll/departments", { method: "PATCH", body: JSON.stringify(row) });

export const getCostCentres = () => apiFetch<{ costCentres: CostCentreRow[] }>("/api/payroll/cost-centres").then((r) => r.costCentres);
export const saveCostCentre = (row: { code: string; name: string; usedFor: string | null; isDefault: boolean; active: boolean }) =>
  apiFetch<{ ok: true }>("/api/payroll/cost-centres", { method: "PATCH", body: JSON.stringify(row) });

export const addFinancialYear = () => apiFetch<{ fyCode: string }>("/api/payroll/years", { method: "POST" });
export const setYearStatus = (fyCode: string, status: string) =>
  apiFetch<{ ok: true }>("/api/payroll/years", { method: "PATCH", body: JSON.stringify({ fyCode, status }) });

/* ---- Formatting ---- */
export function money(n: number): string {
  const s = Math.abs(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (n < 0 ? "-£" : "£") + s;
}
export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
export const employerCost = (d: RunDept) => d.taxablePay + d.erNI + d.erPension;

/* ---- Payroll PDF diagnostic holding area (Admin) --------------------------- */

export interface DiagnosticFile {
  id: number;
  fileName: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedUtc: string;
}

export const getDiagnostics = () =>
  apiFetch<{ files: DiagnosticFile[] }>("/api/payroll/diagnostic").then((r) => r.files);

/** Multipart upload via the existing postForm helper (401 → Entra, like apiFetch). */
export function uploadDiagnostic(file: File) {
  const body = new FormData();
  body.append("file", file);
  return postForm<{ ok: true; id: number }>("/api/payroll/diagnostic", body);
}

export const deleteDiagnostic = (id: number) =>
  apiFetch<{ ok: true }>(`/api/payroll/diagnostic/${id}`, { method: "DELETE" });
